import { useMemo } from 'react';
import { FormatContent } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Stack, Tooltip, TooltipPopover } from 'src/core/ui';
import { IconsFill, WarningOutlineIcon } from 'src/svgs/Viagogo';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { MarketplaceSalePayment, UiMoney } from 'src/WebApiController';

interface DiscrepancyInfo {
  hasDiscrepancy: boolean;
  manualAmount: string;
  actualAmount: string;
  formatId: FormatContentId;
}

export const MarketplacePaymentsDiscrepancyIcon = ({
  marketplaceSalePayment,
}: {
  marketplaceSalePayment: MarketplaceSalePayment;
}) => {
  const discrepancies = useMemo(() => {
    const {
      pmtAmt,
      manualPmtAmt,
      credit,
      manualCreditAmt,
      charge,
      manualChargeAmt,
      proceedsAmt,
      manualProceedsAmt,
    } = marketplaceSalePayment;

    const getAmountString = (amount?: UiMoney | null) =>
      amount?.disp || `${amount?.amt.toString()} ${amount?.currency}`;

    const checkDiscrepancy = ({
      actualAmount,
      manualAmount,
      formatId,
    }: {
      actualAmount?: UiMoney | null;
      manualAmount?: UiMoney | null;
      formatId: FormatContentId;
    }): DiscrepancyInfo => ({
      hasDiscrepancy: !!(
        actualAmount &&
        manualAmount &&
        actualAmount.amt !== manualAmount.amt
      ),
      actualAmount: getAmountString(actualAmount) || '',
      manualAmount: getAmountString(manualAmount) || '',
      formatId,
    });

    return [
      checkDiscrepancy({
        actualAmount: pmtAmt,
        manualAmount: manualPmtAmt,
        formatId: FormatContentId.PaymentTotalDiscrepancy,
      }),
      checkDiscrepancy({
        actualAmount: proceedsAmt,
        manualAmount: manualProceedsAmt,
        formatId: FormatContentId.ProceedsTotalDiscrepancy,
      }),
      checkDiscrepancy({
        actualAmount: credit,
        manualAmount: manualCreditAmt,
        formatId: FormatContentId.CreditTotalDiscrepancy,
      }),
      checkDiscrepancy({
        actualAmount: charge,
        manualAmount: manualChargeAmt,
        formatId: FormatContentId.ChargeTotalDiscrepancy,
      }),
    ].filter((d) => d.hasDiscrepancy && d.manualAmount && d.actualAmount);
  }, [marketplaceSalePayment]);

  if (discrepancies.length === 0) {
    return null;
  }

  return (
    <Tooltip
      align="center"
      triggerContent={
        <div style={{ display: 'inline-flex', alignItems: 'center' }}>
          <WarningOutlineIcon
            fill={IconsFill.textWarning}
            size={vars.iconSize.m}
          />
        </div>
      }
    >
      <Stack gap="m" alignItems="center" direction="column">
        <Stack gap="m" alignItems="center" direction="column">
          {discrepancies.map((discrepancy, index) => (
            <div key={index}>
              <FormatContent
                id={discrepancy.formatId}
                params={[discrepancy.manualAmount, discrepancy.actualAmount]}
              />
            </div>
          ))}
        </Stack>
      </Stack>
    </Tooltip>
  );
};
